import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { useStepUp } from '../auth/StepUpProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { 
  AlertTriangle, Download, Trash2, Shield, FileText, Clock, X, Loader2, 
  CheckCircle2, Cookie, Brain, Mail, Users, History, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const PrivacySettingsTab = () => {
  const { requireStepUp } = useStepUp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionType, setDeletionType] = useState('soft');
  
  // Consent preferences
  const [consentPrefs, setConsentPrefs] = useState({
    marketing_consent: false,
    analytics_consent: false,
    third_party_sharing: false,
    ai_processing: true
  });
  
  // Export options
  const [exportOptions, setExportOptions] = useState({
    include_invoices: true,
    include_bills: true,
    include_transactions: true,
    include_bank_accounts: true,
    include_products: true,
    include_journals: true,
    include_contacts: true,
    include_bank_feed_transactions: true,
    include_other_receipts: true,
    include_other_payments: true,
    format: 'json'
  });

  useEffect(() => {
    fetchDeletionStatus();
    fetchConsentStatus();
  }, []);

  const fetchDeletionStatus = async () => {
    try {
      const response = await axios.get(`${API}/privacy/deletion-status`);
      setDeletionStatus(response.data);
    } catch (error) {
      console.log('Error fetching deletion status');
    }
  };

  const fetchConsentStatus = async () => {
    try {
      const response = await axios.get(`${API}/privacy/consent-status`);
      setConsentStatus(response.data);
      setConsentPrefs({
        marketing_consent: response.data.marketing_consent || false,
        analytics_consent: response.data.analytics_consent || false,
        third_party_sharing: response.data.third_party_sharing || false,
        ai_processing: response.data.ai_processing !== false
      });
    } catch (error) {
      console.log('Error fetching consent status');
    }
  };

  const handleUpdateConsent = async () => {
    setConsentLoading(true);
    try {
      await axios.post(`${API}/privacy/update-consent`, consentPrefs);
      // Mirror the choice into the CookieConsent-scoped localStorage
      // key and flip PostHog so this settings-page toggle actually
      // controls capture (otherwise it only updated the server side).
      try {
        const existing = JSON.parse(localStorage.getItem('cookieConsent') || '{}');
        localStorage.setItem('cookieConsent', JSON.stringify({
          essential: true,
          analytics: !!consentPrefs.analytics_consent,
          marketing: !!existing.marketing,
          timestamp: new Date().toISOString(),
          policy_version: existing.policy_version || '1.0',
        }));
      } catch { /* noop */ }
      try {
        const ph = typeof window !== 'undefined' ? window.posthog : null;
        if (ph) {
          if (consentPrefs.analytics_consent) {
            ph.opt_in_capturing();
            try { ph.startSessionRecording && ph.startSessionRecording(); } catch { /* noop */ }
          } else {
            ph.opt_out_capturing();
            try { ph.stopSessionRecording && ph.stopSessionRecording(); } catch { /* noop */ }
            try { ph.reset(); } catch { /* noop */ }
          }
        }
      } catch { /* noop */ }
      toast.success('Privacy preferences updated successfully');
      fetchConsentStatus();
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setConsentLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await axios.post(`${API}/privacy/export-data`, exportOptions, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = exportOptions.format === 'json' 
        ? `akountfly_export_${new Date().toISOString().split('T')[0]}.json`
        : `akountfly_export_${new Date().toISOString().split('T')[0]}.zip`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully!');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!confirmPassword) {
      toast.error('Please enter your password to confirm');
      return;
    }

    // Step-up: account deletion is the most destructive action - force
    // a fresh biometric or password prompt every time.
    const ok = await requireStepUp({ reason: 'Confirm deletion of your AkountFly account', force: true });
    if (!ok) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/privacy/request-deletion`, {
        deletion_type: deletionType,
        confirm_password: confirmPassword,
        reason: deletionReason || null
      });
      
      const clearAndExit = (delay) => {
        setTimeout(() => {
          localStorage.removeItem('akountfly_token');
          localStorage.removeItem('akountfly_user');
          localStorage.removeItem('akountfly_business');
          localStorage.removeItem('akountfly_refresh_token');
          localStorage.removeItem('akountfly_csrf_token');
          window.location.href = '/';
        }, delay);
      };

      if (response.data.status === 'deleted') {
        toast.success('Your account has been permanently deleted. A confirmation email is on its way. Signing you out…', { duration: 4000 });
        setShowDeleteDialog(false);
        clearAndExit(2500);
      } else if (response.data.status === 'scheduled') {
        const when = response.data.deletion_date ? new Date(response.data.deletion_date).toLocaleDateString() : 'in 30 days';
        toast.success(`Account deletion scheduled for ${when}. A confirmation email is on its way. Signing you out — log back in any time to cancel.`, { duration: 5000 });
        setShowDeleteDialog(false);
        clearAndExit(3000);
      } else {
        toast.success(response.data.message);
        fetchDeletionStatus();
        setShowDeleteDialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process deletion request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setLoading(true);
    try {
      // Iter 938 - Use the unified account-closure endpoint which also
      // reactivates the subscription (the legacy /privacy/cancel-deletion
      // only cleared deletion flags but left status=cancelled, keeping the
      // user billing-locked).
      const res = await axios.post(`${API}/account/cancel-scheduled-deletion`);
      toast.success(res.data?.message || 'Pending deletion cancelled. Your account is fully restored.');
      fetchDeletionStatus();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        toast.error('No pending deletion to cancel.');
      } else if (status === 410) {
        toast.error('The 30-day grace window has expired. Please contact team@akountfly.com.');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to cancel deletion');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Deletion Warning Banner */}
      {deletionStatus?.status === 'scheduled' && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium text-red-800">Account Scheduled for Deletion</p>
                  <p className="text-sm text-red-700">
                    Your account will be deleted on {new Date(deletionStatus.scheduled_deletion_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleCancelDeletion}
                disabled={loading}
                className="border-red-500 text-red-600 hover:bg-red-100"
                data-testid="cancel-scheduled-deletion-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel Deletion'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consent Management - NEW SECTION */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#108981]/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#108981]" />
            </div>
            <div>
              <CardTitle className="text-lg">Privacy Preferences</CardTitle>
              <CardDescription>
                Control how your data is used (GDPR Article 7)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Marketing Consent */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Marketing Communications</p>
                <p className="text-sm text-muted-foreground">Receive product updates and promotional emails</p>
              </div>
            </div>
            <Switch
              checked={consentPrefs.marketing_consent}
              onCheckedChange={(checked) => setConsentPrefs({...consentPrefs, marketing_consent: checked})}
              data-testid="marketing-consent-toggle"
            />
          </div>

          {/* Analytics Consent */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Cookie className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Analytics Cookies</p>
                <p className="text-sm text-muted-foreground">Help us improve by tracking how you use AkountFly</p>
              </div>
            </div>
            <Switch
              checked={consentPrefs.analytics_consent}
              onCheckedChange={(checked) => setConsentPrefs({...consentPrefs, analytics_consent: checked})}
              data-testid="analytics-consent-toggle"
            />
          </div>

          {/* Third Party Sharing */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Third-Party Data Sharing</p>
                <p className="text-sm text-muted-foreground">Allow sharing anonymized data with partners</p>
              </div>
            </div>
            <Switch
              checked={consentPrefs.third_party_sharing}
              onCheckedChange={(checked) => setConsentPrefs({...consentPrefs, third_party_sharing: checked})}
              data-testid="third-party-consent-toggle"
            />
          </div>

          {/* AI Processing */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Brain className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">AI-Powered Features</p>
                <p className="text-sm text-muted-foreground">
                  Use AI for transaction classification and insights.{' '}
                  <Link to="/privacy#ai-processing" className="text-[#108981] hover:underline">Learn more</Link>
                </p>
              </div>
            </div>
            <Switch
              checked={consentPrefs.ai_processing}
              onCheckedChange={(checked) => setConsentPrefs({...consentPrefs, ai_processing: checked})}
              data-testid="ai-processing-toggle"
            />
          </div>

          {!consentPrefs.ai_processing && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Disabling AI will require you to manually classify all transactions.
              </p>
            </div>
          )}

          <Button 
            onClick={handleUpdateConsent}
            disabled={consentLoading}
            className="mt-2"
            data-testid="save-consent-btn"
          >
            {consentLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Preferences
          </Button>

          {consentStatus?.last_updated && (
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date(consentStatus.last_updated).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cookie Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Cookie Settings</CardTitle>
              <CardDescription>Manage your cookie preferences</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            You can review and update your cookie preferences. Changes will take effect on your next visit.
          </p>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => {
                localStorage.removeItem('cookieConsent');
                sessionStorage.removeItem('cookieConsentDismissed');
                toast.success('Cookie preferences reset. Refresh the page to set new preferences.');
              }}
              data-testid="reset-cookies-btn"
            >
              Reset Cookie Preferences
            </Button>
            <Link to="/cookie-policy">
              <Button variant="ghost" className="gap-2">
                <ExternalLink className="w-4 h-4" />
                View Cookie Policy
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* iter1343 - Legal documents accessible from Settings (Apple 5.1.1 iv). */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Legal & Policies</CardTitle>
              <CardDescription>Review the agreements that govern your use of AkountFly</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2">
            <Link to="/privacy-policy" data-testid="legal-privacy-policy-link">
              <Button variant="ghost" className="w-full justify-between text-left">
                <span className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#108981]" />
                  Privacy Policy
                </span>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Link>
            <Link to="/terms-of-service" data-testid="legal-terms-of-service-link">
              <Button variant="ghost" className="w-full justify-between text-left">
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#108981]" />
                  Terms of Service
                </span>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Link>
            <Link to="/cookie-policy" data-testid="legal-cookie-policy-link">
              <Button variant="ghost" className="w-full justify-between text-left">
                <span className="flex items-center gap-2">
                  <Cookie className="w-4 h-4 text-[#108981]" />
                  Cookie Policy
                </span>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Link>
            <Link to="/help" data-testid="legal-help-center-link">
              <Button variant="ghost" className="w-full justify-between text-left">
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#108981]" />
                  Help Center & Support
                </span>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#108981]/10 flex items-center justify-center">
              <Download className="w-5 h-5 text-[#108981]" />
            </div>
            <div>
              <CardTitle className="text-lg">Export Your Data</CardTitle>
              <CardDescription>
                Download a copy of all your data (GDPR Article 20)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Export your invoices, bills, transactions, journals, contacts, and other data in JSON or CSV format.
            This includes all data associated with your account.
          </p>
          <Button 
            onClick={() => setShowExportDialog(true)}
            className="gap-2"
            variant="outline"
            data-testid="export-data-btn"
          >
            <FileText className="w-4 h-4" />
            Export Data
          </Button>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Data Retention</CardTitle>
              <CardDescription>How we store and retain your data</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Financial Records</p>
                <p className="text-sm text-muted-foreground">Invoices, bills, transactions, journals</p>
              </div>
              <Badge variant="secondary">7 years (legal)</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Account Data</p>
                <p className="text-sm text-muted-foreground">Profile, settings, preferences</p>
              </div>
              <Badge variant="secondary">Until deletion</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Session & Activity Data</p>
                <p className="text-sm text-muted-foreground">Login sessions, activity logs</p>
              </div>
              <Badge variant="secondary">90 days</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">AI Classifications</p>
                <p className="text-sm text-muted-foreground">Transaction categorizations</p>
              </div>
              <Badge variant="secondary">Until deletion</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Deletion */}
      <Card className="border-red-100">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-red-600">Close Account</CardTitle>
              <CardDescription>
                Cancel subscription or permanently delete your data (GDPR Article 17)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            We&apos;ll walk you through a short exit survey, give you a chance to export your data,
            and let you choose between cancelling your subscription (data stays for 90 days) or
            permanently deleting your account.
          </p>
          <Button
            variant="destructive"
            onClick={() => navigate('/account/close?from=privacy')}
            className="gap-2"
            data-testid="close-account-btn"
          >
            <Trash2 className="w-4 h-4" />
            Close My Account
          </Button>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Your Data</DialogTitle>
            <DialogDescription>
              Select the data you want to export
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              {[
                { key: 'include_invoices', label: 'Invoices' },
                { key: 'include_bills', label: 'Bills' },
                { key: 'include_transactions', label: 'Bank Transactions' },
                { key: 'include_journals', label: 'Journal Entries' },
                { key: 'include_contacts', label: 'Contacts' },
                { key: 'include_bank_accounts', label: 'Bank Accounts' },
                { key: 'include_products', label: 'Products & Services' },
                { key: 'include_bank_feed_transactions', label: 'Bank Feed Transactions' },
                { key: 'include_other_receipts', label: 'Other Receipts' },
                { key: 'include_other_payments', label: 'Other Payments' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <Label htmlFor={item.key}>{item.label}</Label>
                  <Switch
                    id={item.key}
                    checked={exportOptions[item.key]}
                    onCheckedChange={(checked) => 
                      setExportOptions({ ...exportOptions, [item.key]: checked })
                    }
                  />
                </div>
              ))}
            </div>
            
            <div className="space-y-2">
              <Label>Format</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={exportOptions.format === 'json' ? 'default' : 'outline'}
                  onClick={() => setExportOptions({ ...exportOptions, format: 'json' })}
                  className="flex-1"
                >
                  JSON
                </Button>
                <Button
                  type="button"
                  variant={exportOptions.format === 'csv' ? 'default' : 'outline'}
                  onClick={() => setExportOptions({ ...exportOptions, format: 'csv' })}
                  className="flex-1"
                >
                  CSV (ZIP)
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportData} 
              disabled={exportLoading}
              className="gap-2"
            >
              {exportLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Your Account</DialogTitle>
            <DialogDescription>
              This is a serious action. Please read carefully.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> Deleting your account will remove all invoices, bills, 
                transactions, journals, contacts, and other business data associated with this account.
              </p>
            </div>
            
            <div className="space-y-3">
              <Label>Deletion Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDeletionType('soft')}
                  className={`p-3 border rounded-lg text-left transition-all ${
                    deletionType === 'soft' 
                      ? 'border-[#108981] bg-[#108981]/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-sm">Schedule Deletion</p>
                  <p className="text-xs text-muted-foreground">
                    30-day grace period. Can be cancelled.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setDeletionType('hard')}
                  className={`p-3 border rounded-lg text-left transition-all ${
                    deletionType === 'hard' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-sm text-red-600">Immediate Deletion</p>
                  <p className="text-xs text-muted-foreground">
                    Cannot be undone.
                  </p>
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm your password *</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Enter your account password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="confirm-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="deletion-reason">Reason (optional)</Label>
              <Input
                id="deletion-reason"
                placeholder="Why are you leaving?"
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRequestDeletion}
              disabled={loading || !confirmPassword}
              className="gap-2"
              data-testid="confirm-delete-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {deletionType === 'soft' ? 'Schedule Deletion' : 'Delete Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrivacySettingsTab;
